/* ============================================
   EB WISHLIST — Core Engine
   localStorage-based wishlist with event system
   ============================================ */
(function () {
  'use strict';

  const STORAGE_KEY = 'eb_wishlist';

  /* ---- Storage helpers ---- */
  function getWishlist() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  }

  function saveWishlist(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch {
      // storage full or blocked
    }
  }

  /* ---- Public API ---- */
  const EBWishlist = {
    getAll() {
      return getWishlist();
    },

    has(handle) {
      return getWishlist().includes(handle);
    },

    add(handle) {
      const list = getWishlist();
      if (!list.includes(handle)) {
        list.push(handle);
        saveWishlist(list);
        this._notify(handle, 'added');
      }
    },

    remove(handle) {
      let list = getWishlist();
      const idx = list.indexOf(handle);
      if (idx > -1) {
        list.splice(idx, 1);
        saveWishlist(list);
        this._notify(handle, 'removed');
      }
    },

    toggle(handle) {
      if (this.has(handle)) {
        this.remove(handle);
        return false; // now removed
      } else {
        this.add(handle);
        return true; // now added
      }
    },

    count() {
      return getWishlist().length;
    },

    _notify(handle, action) {
      document.dispatchEvent(
        new CustomEvent('wishlist:changed', {
          detail: { handle, action, count: this.count() },
        })
      );
      this._syncBadge();
      this._syncButtons(handle);
    },

    /* ---- Badge sync ---- */
    _syncBadge() {
      const count = this.count();
      document.querySelectorAll('.wishlist-count').forEach((el) => {
        el.textContent = count;
        el.style.display = count > 0 ? 'flex' : 'none';
      });
    },

    /* ---- Button state sync ---- */
    _syncButtons(handle) {
      const active = this.has(handle);
      document
        .querySelectorAll(`[data-wishlist-btn][data-product-handle="${handle}"]`)
        .forEach((btn) => {
          btn.setAttribute('data-wishlist-active', active ? 'true' : 'false');
          const label = btn.querySelector('.eb-wishlist-pdp__label');
          if (label) {
            label.textContent = active
              ? 'Added to Wishlist'
              : 'Add to Wishlist';
          }
          btn.setAttribute(
            'aria-label',
            active ? 'Remove from wishlist' : 'Add to wishlist'
          );
        });
    },

    /* ---- Initialize all buttons on page ---- */
    initButtons(root) {
      const scope = root || document;
      scope.querySelectorAll('[data-wishlist-btn]').forEach((btn) => {
        const handle = btn.getAttribute('data-product-handle');
        if (!handle) return;

        // Set initial state
        const active = this.has(handle);
        btn.setAttribute('data-wishlist-active', active ? 'true' : 'false');
        const label = btn.querySelector('.eb-wishlist-pdp__label');
        if (label) {
          label.textContent = active ? 'Added to Wishlist' : 'Add to Wishlist';
        }
        btn.setAttribute(
          'aria-label',
          active ? 'Remove from wishlist' : 'Add to wishlist'
        );

        // Bind click (deduplicate with flag)
        if (!btn._ebWishlistBound) {
          btn._ebWishlistBound = true;
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            EBWishlist.toggle(handle);
          });
        }
      });
    },
  };

  /* ---- Init on DOM ready ---- */
  function init() {
    EBWishlist._syncBadge();
    EBWishlist.initButtons();

    // Observe dynamically added product cards (e.g. recommendations, infinite scroll)
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === 1) {
            // Check if the node itself or its children contain wishlist buttons
            if (node.matches?.('[data-wishlist-btn]')) {
              EBWishlist.initButtons(node.parentElement);
            } else if (node.querySelector?.('[data-wishlist-btn]')) {
              EBWishlist.initButtons(node);
            }
          }
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify section render (theme editor + section rendering API)
  document.addEventListener('shopify:section:load', () => {
    EBWishlist.initButtons();
    EBWishlist._syncBadge();
  });

  /* ---- Wishlist Page Renderer ---- */
  EBWishlist.renderPage = async function (containerId) {
    const container = document.getElementById(containerId);
    if (!container) return;

    const handles = this.getAll();

    if (handles.length === 0) {
      renderEmpty(container);
      return;
    }

    // Show loading
    container.innerHTML = `
      <div class="eb-wishlist-loading">
        <div class="eb-wishlist-loading__spinner"></div>
        <span>Loading your wishlist...</span>
      </div>`;

    // Fetch all products
    const products = [];
    for (const handle of handles) {
      try {
        const res = await fetch(`/products/${handle}.js`);
        if (res.ok) {
          products.push(await res.json());
        }
      } catch {
        // skip failed fetches
      }
    }

    if (products.length === 0) {
      renderEmpty(container);
      return;
    }

    renderGrid(container, products);
  };

  function renderEmpty(container) {
    container.innerHTML = `
      <div class="eb-wishlist-empty">
        <svg class="eb-wishlist-empty__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
        </svg>
        <h2 class="eb-wishlist-empty__title">Your wishlist is empty</h2>
        <p class="eb-wishlist-empty__text">Browse our collection and save your favourite pieces</p>
        <a href="/collections/all" class="eb-wishlist-empty__btn">
          Continue Shopping
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
        </a>
      </div>`;
    updatePageCount(0);
  }

  function formatMoney(cents) {
    return '₹' + (cents / 100).toLocaleString('en-IN');
  }

  function renderGrid(container, products) {
    updatePageCount(products.length);

    let html = '<div class="eb-wishlist-page__grid">';

    for (const product of products) {
      const variant = product.variants[0];
      const image = product.featured_image
        ? product.featured_image.replace(/\.([a-z]+)$/i, '_600x.$1')
        : '';
      const url = `/products/${product.handle}`;
      const price = variant.price;
      const comparePrice = variant.compare_at_price;
      const available = product.available;

      let priceHtml = '';
      if (comparePrice && comparePrice > price) {
        priceHtml = `
          <span class="eb-wishlist-card__price-sale">${formatMoney(price)}</span>
          <span class="eb-wishlist-card__price-compare">${formatMoney(comparePrice)}</span>`;
      } else {
        priceHtml = `<span>${formatMoney(price)}</span>`;
      }

      let badgeHtml = '';
      if (!available) {
        badgeHtml =
          '<span class="eb-wishlist-card__badge eb-wishlist-card__badge--sold-out">Sold Out</span>';
      } else if (comparePrice && comparePrice > price) {
        const discount = Math.round(
          ((comparePrice - price) / comparePrice) * 100
        );
        badgeHtml = `<span class="eb-wishlist-card__badge eb-wishlist-card__badge--sale">${discount}% Off</span>`;
      }

      html += `
        <div class="eb-wishlist-card" data-wishlist-card data-product-handle="${product.handle}">
          <button class="eb-wishlist-card__remove" data-wishlist-remove="${product.handle}" aria-label="Remove from wishlist">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
          <a href="${url}" class="eb-wishlist-card__image-wrap">
            ${image ? `<img src="${image}" alt="${product.title}" loading="lazy" width="600" height="800">` : ''}
          </a>
          <div class="eb-wishlist-card__info">
            <a href="${url}" class="eb-wishlist-card__title">${product.title}</a>
            <div class="eb-wishlist-card__price">${priceHtml}</div>
            ${badgeHtml}
          </div>
        </div>`;
    }

    html += '</div>';
    container.innerHTML = html;

    // Bind remove buttons
    container.querySelectorAll('[data-wishlist-remove]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const handle = btn.getAttribute('data-wishlist-remove');
        const card = btn.closest('[data-wishlist-card]');
        if (card) {
          card.classList.add('eb-wishlist-card--removing');
          card.addEventListener(
            'animationend',
            () => {
              EBWishlist.remove(handle);
              card.remove();
              // Check if grid is empty now
              const remaining = container.querySelectorAll('[data-wishlist-card]');
              if (remaining.length === 0) {
                renderEmpty(container);
              } else {
                updatePageCount(remaining.length);
              }
            },
            { once: true }
          );
        }
      });
    });
  }

  function updatePageCount(count) {
    const el = document.querySelector('.eb-wishlist-page__count');
    if (el) {
      el.textContent = count === 1 ? '1 item' : `${count} items`;
    }
  }

  // Expose globally
  window.EBWishlist = EBWishlist;
})();
